import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, Pencil, AlertTriangle, MoreVertical } from 'lucide-react';

function ChapterCard({ chapter, onClick, onDelete, onEdit }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Draft',
      in_progress: 'In Progress',
      polished: 'Polished'
    };
    return labels[status] || 'Draft';
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMenu(!showMenu);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMenu(false);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete(chapter.chapter_id);
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDeleteConfirm(false);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMenu(false);
    if (onEdit) onEdit(chapter);
  };

  const wordCount = chapter.content ? chapter.content.trim().split(/\s+/).filter(Boolean).length : 0;
  
  const statusDotColor = {
    draft: 'bg-[#D4C8BC] dark:bg-[#3A3028]',
    in_progress: 'bg-[#7B6A90] dark:bg-[#B0A0C0]',
    polished: 'bg-emerald-400 dark:bg-emerald-500',
  }[chapter.status] || 'bg-[#D4C8BC]';

  return (
    <>
      <div
        onClick={onClick}
        data-testid={`chapter-card-${chapter.chapter_id}`}
        className="story-surface px-4 sm:px-5 py-3.5 hover:border-[var(--story-line-strong)] transition-all duration-200 cursor-pointer group"
      >
        <div className="flex items-center gap-3 sm:gap-4">
          <span className="text-xs sm:text-sm text-[#B0A898] dark:text-[#6A625B] w-7 flex-shrink-0 tabular-nums font-medium">
            {String(chapter.chapter_number).padStart(2, '0')}
          </span>

          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-base sm:text-lg font-semibold text-[#2A2420] dark:text-[#F0EBE4] truncate">
              {chapter.title}
            </h3>
            <div className="mt-1 flex items-center gap-2 text-xs text-[#958B80] dark:text-[#6D655E]">
              <span className={`w-1.5 h-1.5 rounded-full ${statusDotColor}`} />
              <span>{getStatusLabel(chapter.status)}</span>
              {wordCount > 0 && (
                <>
                  <span aria-hidden="true">-</span>
                  <span className="tabular-nums">{wordCount.toLocaleString()} words</span>
                </>
              )}
            </div>
          </div>

          <div className="relative flex-shrink-0">
            <button
              onClick={handleMenuClick}
              data-testid="chapter-menu-button"
              className="opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity min-h-[40px] min-w-[40px] flex items-center justify-center hover:bg-[#E8E0D4]/60 dark:hover:bg-[#2A2520]/50 rounded-xl"
              aria-label="Chapter actions"
            >
              <MoreVertical className="w-4 h-4 text-[#7C766E] dark:text-[#8C847A]" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-10 w-40 bg-white dark:bg-[#22201C] border border-[#E0D8CC] dark:border-[#2A2520]/50 rounded-xl shadow-lg z-50 overflow-hidden">
                <button
                  onClick={handleEdit}
                  className="w-full px-4 py-3 text-left text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] hover:bg-[#F0EBE4]/50 dark:hover:bg-[#2A2520]/50 transition-colors flex items-center space-x-2 border-b border-[#E0D8CC] dark:border-[#2A2520]/50"
                >
                  <Pencil className="w-4 h-4 text-[#5C4D6B] dark:text-[#B0A0C0]" />
                  <span>Edit Title</span>
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="w-full px-4 py-3 text-left text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center space-x-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#22201C] rounded-2xl p-6 max-w-md w-full shadow-2xl border-2 border-red-200 dark:border-red-800">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Delete Chapter?</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete "<strong className="text-gray-900 dark:text-gray-100">{chapter.title}</strong>"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <Button
                onClick={handleCancelDelete}
                variant="outline"
                className="flex-1 font-semibold border-2 dark:border-[#2A2520] dark:text-gray-200 dark:hover:bg-[#2A2520]/50"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white font-semibold shadow-lg"
              >
                Delete Chapter
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ChapterCard;
